import { useState, useEffect } from 'react';
import { useMsal } from '@azure/msal-react';
import { spRequest, loginRequest } from '../authConfig';
import { submitWin, uploadAttachment, getCurrentUserDepartment, getUserDepartmentFromProfile } from '../graphService';
import UserSearch from './UserSearch';
import RichTextEditor from './RichTextEditor';
import DropZone from './DropZone';
import '../styles/SubmitForm.css';

// Canonical departments used to normalize the value captured from the directory.
const DEPARTMENTS = [
  'Accounting', 'Bleu Cheese Crumbles', 'Bleu Cheese Wheels', 'Buttermilk',
  'Customer Support', 'Formulation', 'General Admin',
  'Human Resources', 'IT', 'Innovation', 'Inventory Control', 'Logistics',
  'Maintenance', 'Marketing', 'Operation Support', 'Packaging',
  'Planning & Scheduling', 'Plant General', 'Portion Control', 'Purchasing',
  'Quality Assurance', 'Quality Control', 'Regulatory', 'Research and Development',
  'Sales', 'Sanitation', 'Transportation', 'Truck Shop', 'Warehouse', 'Waste Water',
];

const FREQUENCIES = ['One-time', 'Daily', 'Weekly', 'Monthly'];

const TASK_MAX = 55;

const EMPTY = {
  department: '',
  task: '',
  howDidYouUseAI: '',
  benefits: '',
  timeSavedHrs: '',
  timeSavedMins: '',
  valueCapacity: false,
  valueBenefit: false,
  frequency: '',
  onBehalfOfName: '',
  onBehalfOfEmail: '',
};

export default function SubmitForm({ siteId, listId }) {
  const { instance, accounts } = useMsal();
  const [form, setForm] = useState(EMPTY);
  const [attachments, setAttachments] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState(null);
  const [onBehalf, setOnBehalf] = useState(false);

  const normalizeDept = (s) => s.toLowerCase()
    .replace(/^\d+\s*-\s*/, '')
    .replace(/\bcorp\.?\s*/g, '')
    .replace(/\band\b/g, '')
    .replace(/[^a-z\s]/g, '')
    .replace(/\s+/g, ' ')
    .trim();

  const matchDepartment = (dept) => {
    const n = normalizeDept(dept);
    if (!n) return null;
    // 1. Exact normalized match wins outright.
    const exact = DEPARTMENTS.find((d) => normalizeDept(d) === n);
    if (exact) return exact;
    // 2. Whole-word match only — avoids false hits like short "IT" landing inside
    //    "quality". A department matches if all its words appear as words in the
    //    input, or the input's words are all words of the department name.
    const inputWords = new Set(n.split(' '));
    return DEPARTMENTS.find((d) => {
      const deptWords = normalizeDept(d).split(' ');
      const deptSet = new Set(deptWords);
      return deptWords.every((w) => inputWords.has(w))
        || [...inputWords].every((w) => deptSet.has(w));
    }) || null;
  };

  // Department is captured behind the scenes (the field is no longer on the form).
  // Prefer the canonical match; fall back to the raw directory value so it is never lost.
  const resolveDept = (raw) => (raw ? (matchDepartment(raw) || raw.trim()) : '');

  useEffect(() => {
    async function loadDept() {
      try {
        const tokenRes = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        const dept = await getCurrentUserDepartment(tokenRes.accessToken);
        if (dept) setForm((f) => ({ ...f, department: resolveDept(dept) }));
      } catch { /* silently ignore — department is best-effort */ }
    }
    loadDept();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  const handleBehalfChange = async ({ name, email, id }) => {
    setForm((f) => ({ ...f, onBehalfOfName: name, onBehalfOfEmail: email }));
    if (id && email) {
      // On-behalf: department follows the person the win is for, not the submitter.
      try {
        const tokenRes = await instance.acquireTokenSilent({ ...spRequest, account: accounts[0] });
        const dept = await getUserDepartmentFromProfile(tokenRes.accessToken, email);
        if (dept) setForm((f) => ({ ...f, department: resolveDept(dept) }));
      } catch { /* silently ignore */ }
    } else if (!name) {
      // Cleared — revert to the current user's own department.
      try {
        const tokenRes = await instance.acquireTokenSilent({ ...loginRequest, account: accounts[0] });
        const dept = await getCurrentUserDepartment(tokenRes.accessToken);
        setForm((f) => ({ ...f, department: resolveDept(dept) }));
      } catch { /* silently ignore */ }
    }
  };

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
  };

  const toggleCapacity = () => {
    setForm((f) => f.valueCapacity
      ? { ...f, valueCapacity: false, timeSavedHrs: '', timeSavedMins: '' }
      : { ...f, valueCapacity: true });
  };

  const toggleBenefit = () => {
    setForm((f) => f.valueBenefit
      ? { ...f, valueBenefit: false, benefits: '' }
      : { ...f, valueBenefit: true });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSubmitting(true);
    setError(null);

    try {
      const tokenRes = await instance.acquireTokenSilent({
        ...spRequest,
        account: accounts[0],
      });

      const timeSaved = form.valueCapacity
        ? (parseInt(form.timeSavedHrs) || 0) * 60 + (parseInt(form.timeSavedMins) || 0)
        : 0;

      const item = await submitWin(tokenRes.accessToken, siteId, listId, {
        department: form.department,
        task: form.task,
        howDidYouUseAI: form.howDidYouUseAI,
        benefits: form.valueBenefit ? form.benefits : '',
        timeSaved,
        frequency: form.frequency,
        onBehalfOfEmail: form.onBehalfOfEmail,
      });

      if (attachments.length > 0 && item?.Id) {
        for (const file of attachments) {
          await uploadAttachment(tokenRes.accessToken, item.Id, file);
        }
      }

      setSuccess(true);
      setForm(EMPTY);
      setAttachments([]);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  if (success) {
    return (
      <div className="submit-success">
        <div className="success-icon">✓</div>
        <h2>Win Submitted!</h2>
        <p>Thanks for sharing how AI is making an impact.</p>
        <button className="btn-primary" onClick={() => setSuccess(false)}>
          Submit Another
        </button>
      </div>
    );
  }

  const taskRemaining = TASK_MAX - form.task.length;

  return (
    <div className="submit-container">
      <div className="submit-card">
        <h2 className="submit-heading">Log an AI Win</h2>
        <p className="submit-subheading">Tell us how AI helped you work smarter.</p>
        <form onSubmit={handleSubmit} className="submit-form">

          <div className="form-group">
            <label htmlFor="task">1. Task Completed *</label>
            <p className="field-hint">Briefly describe the task or project you worked on.</p>
            <input
              id="task"
              name="task"
              type="text"
              required
              maxLength={TASK_MAX}
              placeholder="Enter your answer"
              value={form.task}
              onChange={handleChange}
            />
            <p className="char-counter">{taskRemaining} character{taskRemaining === 1 ? '' : 's'} remaining</p>
          </div>

          <div className="form-group">
            <label>2. How Did You Use AI? *</label>
            <p className="field-hint">Share the AI tool, your prompt (if used), and steps you took.</p>
            <RichTextEditor
              value={form.howDidYouUseAI}
              onChange={(html) => setForm((f) => ({ ...f, howDidYouUseAI: html }))}
              placeholder="Enter your answer"
              rows={4}
            />
          </div>

          <div className="behalf-toggle-section">
            <button
              type="button"
              className={`behalf-toggle ${onBehalf ? 'active' : ''}`}
              onClick={async () => {
                setOnBehalf((v) => !v);
                if (onBehalf) await handleBehalfChange({ name: '', email: '', id: '' });
              }}
            >
              {onBehalf ? '✕  Cancel' : '+ Submit AI Win on Behalf of Someone'}
            </button>

            {onBehalf && (
              <div className="behalf-field">
                <p className="field-hint">Search for the colleague you are submitting this win for.</p>
                <UserSearch
                  value={form.onBehalfOfName}
                  onChange={handleBehalfChange}
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>3. What value did this AI win create?</label>
            <p className="field-hint">Select all that apply — a win can free up capacity and deliver a non-time benefit.</p>
            <div className="value-type-toggle">
              <button
                type="button"
                className={`value-type-btn ${form.valueCapacity ? 'selected' : ''}`}
                aria-pressed={form.valueCapacity}
                onClick={toggleCapacity}
              >
                ⏱ Hours Added to Capacity
              </button>
              <button
                type="button"
                className={`value-type-btn ${form.valueBenefit ? 'selected' : ''}`}
                aria-pressed={form.valueBenefit}
                onClick={toggleBenefit}
              >
                ✦ Non-Time Benefit
              </button>
            </div>

            {form.valueCapacity && (
              <div className="value-type-body">
                <p className="field-hint">How much time did this free up?</p>
                <div className="time-input-row">
                  <div className="time-input-group">
                    <input type="number" name="timeSavedHrs" min="0" placeholder="0" value={form.timeSavedHrs} onChange={handleChange} />
                    <span className="time-unit">hrs</span>
                  </div>
                  <div className="time-input-group">
                    <input type="number" name="timeSavedMins" min="0" max="59" placeholder="0" value={form.timeSavedMins} onChange={handleChange} />
                    <span className="time-unit">min</span>
                  </div>
                </div>
              </div>
            )}

            {form.valueBenefit && (
              <div className="value-type-body">
                <p className="field-hint">Describe the benefit: improved quality, better insights, increased accuracy, etc.</p>
                <RichTextEditor
                  value={form.benefits}
                  onChange={(html) => setForm((f) => ({ ...f, benefits: html }))}
                  placeholder="Describe the benefit..."
                  rows={3}
                />
              </div>
            )}
          </div>

          <div className="form-group">
            <label>4. How Often Do You Use This AI Task? *</label>
            <div className="radio-group">
              {FREQUENCIES.map((f) => (
                <label key={f} className={`radio-option ${form.frequency === f ? 'selected' : ''}`}>
                  <input
                    type="radio"
                    name="frequency"
                    value={f}
                    required
                    checked={form.frequency === f}
                    onChange={handleChange}
                  />
                  {f}
                </label>
              ))}
            </div>
          </div>

          <div className="form-group">
            <label>5. Attachments</label>
            <p className="field-hint">Optional — attach screenshots or images to illustrate your win.</p>
            <DropZone onFiles={(files) => setAttachments((prev) => [...prev, ...files])} />
            {attachments.length > 0 && (
              <ul className="attachment-list">
                {attachments.map((f, i) => (
                  <li key={i}>
                    {f.name}
                    <button
                      type="button"
                      className="attachment-remove-btn"
                      onClick={() => setAttachments((prev) => prev.filter((_, idx) => idx !== i))}
                      aria-label="Remove"
                    >✕</button>
                  </li>
                ))}
              </ul>
            )}
          </div>

          {error && <div className="form-error">{error}</div>}

          <button type="submit" className="btn-primary btn-submit" disabled={submitting}>
            {submitting ? 'Submitting...' : 'Submit My Win'}
          </button>
        </form>
      </div>
    </div>
  );
}
